/**
 * ContextEngineCard + LogoUploader + BrandPaletteField + PaletteSwatch
 * + ContextEngineView + AppliedContextStrip — the Context Engine tab
 * and its persisted brand profile helpers.
 *
 * Extracted from App.js during P1 Phase-3 refactor. Zero behavioural
 * changes.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

import { API, extractErrorDetail, INDUSTRIES, JURISDICTIONS, FREE_TEXT_PLACEHOLDER } from "../lib/beaconUtils";

export function ContextEngineCard({ form, setForm, toggleJurisdiction,
                            profileIsDirty, appliedAt, applyContext,
                            profileValid,
                            authUser, savingProfile, savedAt }) {
  const stamp = appliedAt
    ? appliedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;
  const savedStamp = savedAt
    ? savedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <section className="beacon-card" data-testid="context-engine-card">
      <div className="beacon-card-eyebrow">CONTEXT ENGINE</div>
      <h2 className="beacon-card-title">Tell Beacon about your site.</h2>
      <p className="beacon-card-lede">
        Industry, jurisdiction and a few lines of context. The rest happens
        behind the scenes.
      </p>

      <div className="beacon-field">
        <label className="beacon-label">Site, project or client name</label>
        <input
          className="beacon-input"
          data-testid="input-site-name"
          value={form.site_name}
          onChange={(e) => setForm({ ...form, site_name: e.target.value })}
          placeholder="e.g. Acme Civil — Western Highway Upgrade"
        />
      </div>

      <div className="beacon-field">
        <label className="beacon-label">Industry</label>
        <select
          className="beacon-input"
          data-testid="select-industry"
          value={form.industry}
          onChange={(e) => setForm({ ...form, industry: e.target.value })}
        >
          {INDUSTRIES.map((i) => (
            <option key={i} value={i}>{i}</option>
          ))}
        </select>
      </div>

      <div className="beacon-field">
        <label className="beacon-label">
          Jurisdiction(s) <span className="beacon-required">required</span>
        </label>
        <div className="beacon-checks">
          {JURISDICTIONS.map((j) => {
            const active = form.jurisdictions.includes(j);
            return (
              <button
                key={j}
                type="button"
                data-testid={`jurisdiction-${j}`}
                className={`beacon-chip${active ? " is-active" : ""}`}
                onClick={() => toggleJurisdiction(j)}
              >{j}</button>
            );
          })}
        </div>
        {form.jurisdictions.includes("VIC") && (
          <p className="beacon-hint" data-testid="vic-routing-hint">
            Victoria selected → outputs route to WorkSafe Victoria framework
            (OHS Act 2004), not the Model WHS Codes.
          </p>
        )}
      </div>

      <div className="beacon-field">
        <label className="beacon-label">Anything else worth knowing?</label>
        <textarea
          className="beacon-input beacon-textarea"
          data-testid="input-free-text"
          rows={5}
          maxLength={500}
          placeholder={FREE_TEXT_PLACEHOLDER}
          value={form.free_text_context}
          onChange={(e) => setForm({ ...form, free_text_context: e.target.value })}
        />
        <span className="beacon-charcount">{form.free_text_context.length}/500</span>
      </div>

      <div className="beacon-field">
        <label className="beacon-label">Real site examples (optional)</label>
        <textarea
          className="beacon-input beacon-textarea"
          data-testid="input-real-examples"
          rows={4}
          maxLength={1200}
          placeholder={"Paste 2-3 genuine, de-identified near-misses or incidents from your sites - one per line. Beacon weaves real events into the comms instead of made-up examples.\ne.g. Forklift reversed out of the racking aisle while a picker was walking through - horn not used, no contact."}
          value={form.real_examples || ""}
          onChange={(e) => setForm({ ...form, real_examples: e.target.value })}
        />
        <span className="beacon-charcount">{(form.real_examples || "").length}/1200</span>
        <p className="beacon-hint">
          No names, no site identifiers — roles only. Real events land far
          harder than hypotheticals.
        </p>
      </div>

      <div className="beacon-field">
        <label className="beacon-label">Audience &amp; environment</label>
        <div className="beacon-checks">
          {[
            ["field", "Field / Site"],
            ["office", "Head Office"],
            ["mixed", "Mixed"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              data-testid={`audience-${key}`}
              className={`beacon-chip${(form.audience || "field") === key ? " is-active" : ""}`}
              onClick={() => setForm({ ...form, audience: key })}
            >{label}</button>
          ))}
        </div>
        <p className="beacon-hint">
          Sets the vocabulary. Head Office drops the site slang entirely;
          Field leans into pre-start and toolbox language.
        </p>
      </div>

      <div className="beacon-field">
        <label className="beacon-label">Tone preference</label>
        <div className="beacon-checks">
          {["conversational", "direct", "formal"].map((t) => (
            <button
              key={t}
              type="button"
              data-testid={`tone-${t}`}
              className={`beacon-chip${form.tone === t ? " is-active" : ""}`}
              onClick={() => setForm({ ...form, tone: t })}
            >{t.charAt(0).toUpperCase() + t.slice(1)}</button>
          ))}
        </div>
      </div>

      <div className="beacon-field">
        <label className="beacon-label">Company footer line (optional)</label>
        <input
          className="beacon-input"
          data-testid="input-company-footer"
          value={form.company_footer}
          onChange={(e) => setForm({ ...form, company_footer: e.target.value })}
          placeholder="e.g. Acme Construction  ·  Health & Safety"
          maxLength={120}
        />
      </div>

      <div className="beacon-field">
        <label className="beacon-label">
          Company logo (optional)
          <span className="beacon-required">PNG · JPG · ≤ 1 MB</span>
        </label>
        <LogoUploader
          value={form.company_logo_b64}
          onChange={(b64) => setForm({ ...form, company_logo_b64: b64 })}
        />
        <p className="beacon-hint">
          Used on the toolbox handout cover and the poster top-right. Square or
          landscape marks land best.
        </p>
      </div>

      <BrandPaletteField
        value={form.brand_palette}
        onChange={(palette) => setForm({ ...form, brand_palette: palette })}
      />

      <div className="beacon-apply-bar" data-testid="apply-bar">
        <div className="beacon-apply-status">
          {profileIsDirty ? (
            <span className="beacon-apply-status-dirty" data-testid="apply-status-dirty">
              ● Unsaved changes
            </span>
          ) : stamp ? (
            <span className="beacon-apply-status-clean" data-testid="apply-status-clean">
              ✓ Context applied at {stamp}
            </span>
          ) : (
            <span className="beacon-apply-status-idle" data-testid="apply-status-idle">
              No context applied yet
            </span>
          )}
          {authUser && (
            <span
              className="beacon-apply-sync"
              data-testid="apply-sync-status"
              title={
                savingProfile
                  ? "Saving to your account…"
                  : savedAt
                  ? `Saved to your account at ${savedStamp}`
                  : "Applying will save this to your account"
              }
            >
              {savingProfile ? (
                <><span className="beacon-spinner" /> Saving…</>
              ) : savedAt ? (
                <>☁ Saved to {authUser.email} at {savedStamp}</>
              ) : (
                <>☁ Will save to {authUser.email} on Apply</>
              )}
            </span>
          )}
        </div>
        <button
          type="button"
          className="beacon-cta beacon-cta-compact"
          data-testid="apply-context-button"
          disabled={!profileIsDirty || !profileValid || savingProfile}
          onClick={applyContext}
        >
          {savingProfile
            ? "Saving…"
            : profileIsDirty
            ? "Apply context to Comms →"
            : "Context up to date"}
        </button>
      </div>
    </section>
  );
}

function LogoUploader({ value, onChange }) {
  const [error, setError] = useState(null);

  const onPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpe?g|svg\+xml)$/i.test(file.type)) {
      setError("Use PNG, JPG or SVG.");
      e.target.value = "";
      return;
    }
    if (file.size > 1024 * 1024) {
      setError("Logo must be 1 MB or smaller.");
      e.target.value = "";
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.onerror = () => setError("Could not read the file.");
    reader.readAsDataURL(file);
  };

  const clear = () => onChange(null);

  return (
    <div className="beacon-logo-uploader" data-testid="logo-uploader">
      {value ? (
        <div className="beacon-logo-preview">
          <img
            src={value}
            alt="Company logo preview"
            data-testid="logo-preview-img"
          />
          <button
            type="button"
            className="beacon-secondary-btn"
            data-testid="logo-clear"
            onClick={clear}
          >
            Remove
          </button>
        </div>
      ) : (
        <label className="beacon-file-drop beacon-file-drop-compact">
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml"
            onChange={onPick}
            data-testid="logo-input"
            style={{ display: "none" }}
          />
          <span className="beacon-file-drop-hint">
            Click to upload logo
            <small>PNG · JPG · SVG · ≤ 1 MB</small>
          </span>
        </label>
      )}
      {error && (
        <div className="beacon-logo-error" data-testid="logo-error">
          {error}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Brand palette field — three colour swatches + PPTX template extraction
// ---------------------------------------------------------------------------

const BEACON_DEFAULT_PALETTE = {
  primary: "#0F0F12",
  accent: "#E86A33",
  text: "#0F0F12",
};

function BrandPaletteField({ value, onChange }) {
  const palette = value || BEACON_DEFAULT_PALETTE;
  const [uploading, setUploading] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  const [lastExtractedFrom, setLastExtractedFrom] = useState(null);

  const isCustom =
    palette.primary?.toUpperCase() !== BEACON_DEFAULT_PALETTE.primary ||
    palette.accent?.toUpperCase() !== BEACON_DEFAULT_PALETTE.accent ||
    palette.text?.toUpperCase() !== BEACON_DEFAULT_PALETTE.text;

  const update = (key, hex) => {
    // Accept anything the user types; the backend re-validates the format
    // before applying. This keeps typing responsive.
    onChange({ ...palette, [key]: hex });
  };

  const resetOne = (key) => {
    onChange({ ...palette, [key]: BEACON_DEFAULT_PALETTE[key] });
  };

  const resetAll = () => {
    onChange(null);
    setLastExtractedFrom(null);
    setExtractionError(null);
  };

  const onTemplatePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";  // allow re-picking the same file
    setUploading(true);
    setExtractionError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/palette/extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30_000,
      });
      onChange({
        primary: r.data.primary,
        accent: r.data.accent,
        text: r.data.text,
      });
      setLastExtractedFrom(file.name);
    } catch (err) {
      setExtractionError(await extractErrorDetail(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="beacon-field" data-testid="brand-palette-field">
      <label className="beacon-label">
        Brand palette (optional)
        <span className="beacon-required">3 colours · applied to every output</span>
      </label>

      <div className="beacon-palette-row" data-testid="palette-row">
        <PaletteSwatch
          testid="palette-primary"
          label="Primary"
          sub="Title bands · poster spine · headings"
          hex={palette.primary}
          onChange={(hex) => update("primary", hex)}
          onReset={() => resetOne("primary")}
        />
        <PaletteSwatch
          testid="palette-accent"
          label="Accent"
          sub="Rules · call-outs · pull-quotes"
          hex={palette.accent}
          onChange={(hex) => update("accent", hex)}
          onReset={() => resetOne("accent")}
        />
        <PaletteSwatch
          testid="palette-text"
          label="Text"
          sub="Headline & body ink"
          hex={palette.text}
          onChange={(hex) => update("text", hex)}
          onReset={() => resetOne("text")}
        />
      </div>

      <div className="beacon-palette-actions">
        <label className="beacon-secondary-btn beacon-palette-upload">
          <input
            type="file"
            accept=".pptx"
            onChange={onTemplatePick}
            disabled={uploading}
            data-testid="palette-pptx-upload"
            style={{ display: "none" }}
          />
          {uploading ? (
            <><span className="beacon-spinner" /> Reading template…</>
          ) : (
            <>Extract from corporate PPTX template →</>
          )}
        </label>
        {isCustom && (
          <button
            type="button"
            className="beacon-secondary-btn-link"
            data-testid="palette-reset"
            onClick={resetAll}
          >
            Reset to Beacon default
          </button>
        )}
      </div>

      {lastExtractedFrom && !extractionError && (
        <p className="beacon-hint" data-testid="palette-extract-status">
          Palette pulled from <strong>{lastExtractedFrom}</strong> — tweak any
          swatch above if the extracted values aren&apos;t quite right.
        </p>
      )}
      {extractionError && (
        <div className="beacon-alert beacon-alert-soft"
             data-testid="palette-extract-error">
          {extractionError}
        </div>
      )}
      {!isCustom && !lastExtractedFrom && (
        <p className="beacon-hint">
          Leave as-is to use Beacon&apos;s ember/cream palette. Pick colours
          manually or upload your corporate PPTX template and Beacon will
          read the theme colours out.
        </p>
      )}
    </div>
  );
}

function PaletteSwatch({ testid, label, sub, hex, onChange, onReset }) {
  const safe = /^#[0-9a-fA-F]{6}$/.test(hex || "") ? hex : "#000000";
  return (
    <div className="beacon-palette-swatch" data-testid={testid}>
      <label className="beacon-palette-swatch-label">
        <span className="beacon-palette-swatch-name">{label}</span>
        <span className="beacon-palette-swatch-sub">{sub}</span>
      </label>
      <div className="beacon-palette-swatch-row">
        <label
          className="beacon-palette-swatch-chip"
          style={{ background: safe }}
        >
          <input
            type="color"
            value={safe}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            data-testid={`${testid}-picker`}
          />
        </label>
        <input
          type="text"
          className="beacon-input beacon-palette-hex"
          value={hex || ""}
          onChange={(e) => {
            let v = e.target.value.trim();
            if (v && !v.startsWith("#")) v = "#" + v;
            onChange(v.toUpperCase());
          }}
          maxLength={7}
          spellCheck="false"
          data-testid={`${testid}-hex`}
        />
        <button
          type="button"
          className="beacon-palette-swatch-reset"
          onClick={onReset}
          title="Reset to Beacon default"
          data-testid={`${testid}-reset`}
        >
          ↺
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Context Engine view (dedicated tab)
// ---------------------------------------------------------------------------

export function ContextEngineView({ form, setForm, toggleJurisdiction,
                             profileIsDirty, appliedAt, applyContext,
                             profileValid,
                             authUser, savingProfile, savedAt }) {
  return (
    <div className="beacon-context-page" data-testid="context-engine-view">
      <ContextEngineCard
        form={form}
        setForm={setForm}
        toggleJurisdiction={toggleJurisdiction}
        profileIsDirty={profileIsDirty}
        appliedAt={appliedAt}
        applyContext={applyContext}
        profileValid={profileValid}
        authUser={authUser}
        savingProfile={savingProfile}
        savedAt={savedAt}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick Comms (generate card + applied-context strip + workspace)
// ---------------------------------------------------------------------------

export function AppliedContextStrip({ appliedProfile, appliedAt, onEditContext }) {
  if (!appliedProfile) {
    return (
      <section
        className="beacon-context-strip beacon-context-strip-empty"
        data-testid="context-strip-empty"
      >
        <div className="beacon-context-strip-body">
          <div className="beacon-context-strip-eyebrow">CONTEXT ENGINE</div>
          <div className="beacon-context-strip-title">
            No context applied yet.
          </div>
          <div className="beacon-context-strip-sub">
            Beacon needs to know your industry, jurisdiction and a few lines
            of site flavour before it can build a comms pack that lands.
          </div>
        </div>
        <button
          type="button"
          className="beacon-cta beacon-cta-compact"
          data-testid="open-context-engine"
          onClick={onEditContext}
        >
          Set up Context Engine →
        </button>
      </section>
    );
  }
  const stamp = appliedAt
    ? appliedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;
  return (
    <section
      className="beacon-context-strip"
      data-testid="context-strip-applied"
    >
      <div className="beacon-context-strip-body">
        <div className="beacon-context-strip-eyebrow">
          ACTIVE CONTEXT{stamp ? ` · APPLIED ${stamp}` : ""}
        </div>
        <div className="beacon-context-strip-title">
          {appliedProfile.site_name || "(unnamed site)"}
        </div>
        <div className="beacon-context-strip-sub">
          {appliedProfile.industry}
          {" · "}
          {(appliedProfile.jurisdictions || []).join(", ") || "no jurisdiction"}
          {" · "}
          {appliedProfile.tone}
        </div>
      </div>
      <button
        type="button"
        className="beacon-secondary-btn"
        data-testid="edit-context"
        onClick={onEditContext}
      >
        Edit context
      </button>
    </section>
  );
}
